import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAnxietyAssessment } from "../../../store/slices/anxietySlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function AnxietyFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentAnxietyAssessment, isLoading, error } = useSelector((state) => state.anxiety || {});

    useEffect(() => {
        if (id) dispatch(fetchAnxietyAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentAnxietyAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentAnxietyAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Anxiety Assessment...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentAnxietyAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentAnxietyAssessment;

    const symptoms = [
        { field: "numbness_or_tingling", label: "Numbness or tingling" },
        { field: "feeling_hot", label: "Feeling hot" },
        { field: "wobbliness_in_legs", label: "Wobbliness in legs" },
        { field: "unable_to_relax", label: "Unable to relax" },
        { field: "fear_of_worst_happening", label: "Fear of worst happening" },
        { field: "dizzy_or_lightheaded", label: "Dizzy or lightheaded" },
        { field: "heart_pounding_racing", label: "Heart pounding / racing" },
        { field: "unsteady", label: "Unsteady" },
        { field: "terrified_or_afraid", label: "Terrified or afraid" },
        { field: "nervous", label: "Nervous" },
        { field: "feeling_of_choking", label: "Feeling of choking" },
        { field: "hands_trembling", label: "Hands trembling" },
        { field: "shaky_unsteady", label: "Shaky / unsteady" },
        { field: "fear_of_losing_control", label: "Fear of losing control" },
        { field: "difficulty_in_breathing", label: "Difficulty in breathing" },
        { field: "fear_of_dying", label: "Fear of dying" },
        { field: "scared", label: "Scared" },
        { field: "indigestion", label: "Indigestion" },
        { field: "faint_lightheaded", label: "Faint / lightheaded" },
        { field: "face_flushed", label: "Face flushed" },
        { field: "hot_cold_sweats", label: "Hot / cold sweats" }
    ];

    const getActualScore = (id) => {
        const val = parseInt(id);
        if (val === 1) return 0;
        if (val === 2) return 1;
        if (val === 3) return 2;
        if (val === 4) return 3;
        return 0; // Default fallback
    };

    const getScoreLabel = (id) => {
        const val = parseInt(id);
        if (val === 1) return "Not at all";
        if (val === 2) return "Mildly – it didn't bother me much";
        if (val === 3) return "Moderately – it was very unpleasant, but I could stand it";
        if (val === 4) return "Severely – it bothered me a lot";
        return "-";
    };

    // Scoring Logic for Print
    const calculateTotal = () => {
        let total = 0;
        symptoms.forEach(s => {
            total += getActualScore(d[s.field]);
        });
        return total;
    };

    const totalScore = calculateTotal();

    const getInterpretation = (score) => {
        if (score <= 21) return "Low Anxiety";
        if (score <= 35) return "Moderate Anxiety";
        return "Potentially Concerning Levels of Anxiety";
    };

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 12px !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
                .header h6 { font-size: 1rem !important; font-weight: bold !important; text-decoration: underline !important; }

                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 8px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 20% !important; }

                .symptom-table { width: 100% !important; border-collapse: collapse !important; }
                .symptom-table th, .symptom-table td { border: 1px solid #000 !important; padding: 6px 10px !important; }
                .symptom-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                
                .score-cell { text-align: center !important; font-weight: bold !important; width: 100px !important; }
                .val-cell { text-align: center !important; width: 150px !important; }

                .result-box { margin-top: 25px !important; border: 2px solid #000 !important; padding: 15px !important; background: #f9f9f9 !important; display: flex !important; justify-content: space-between !important; align-items: center !important; }
                .total-score { font-size: 1.4rem !important; font-weight: bold !important; }
                .interpretation { font-size: 1.2rem !important; font-weight: bold !important; text-decoration: underline !important; text-transform: uppercase !important; }

                .footer { margin-top: 40px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 10px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }

                @media print {
                    .symptom-table tr { page-break-inside: avoid !important; }
                }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>BECK ANXIETY INVENTORY (BAI) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Diagnosis</td>
                            <td>{d.diagnosis}</td>
                            <td className="label-bg">Physician</td>
                            <td>{d.attending_physician}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="symptom-table">
                    <thead>
                        <tr>
                            <th style={{ width: '50px' }}>S#</th>
                            <th>Symptom Description</th>
                            <th className="score-cell">Score (0-3)</th>
                            <th className="val-cell">Severity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {symptoms.map((s, index) => {
                            const rawId = d[s.field];
                            return (
                                <tr key={s.field}>
                                    <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                    <td>{s.label}</td>
                                    <td className="score-cell">{getActualScore(rawId)}</td>
                                    <td className="val-cell" style={{ fontSize: '10px' }}>
                                        {getScoreLabel(rawId)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className="result-box">
                    <div>
                        <span style={{ display: 'block', fontSize: '10px' }}>CUMULATIVE SCORE</span>
                        <div className="total-score">Total Score: {totalScore}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <span style={{ display: 'block', fontSize: '10px' }}>CLINICAL INTERPRETATION</span>
                        <div className="interpretation">{getInterpretation(totalScore)}</div>
                    </div>
                </div>

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Assessing Clinician Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}